import { __, sprintf } from '@wordpress/i18n';

/**
 * Lazily injects the Freemius Checkout.js script into the page (idempotent).
 *
 * @param {bool|null} isProduction
 * @return {Promise<void>}
 */
export const loadCheckoutScript = (isProduction) => {
    return new Promise((resolve) => {
        if (window.FS && window.FS.Checkout) {
            resolve();
            return;
        }
        const script = document.createElement('script');
        script.src = (isProduction !== false
            ? 'https://checkout.freemius.com'
            : 'http://checkout.freemius-local.com:8080'
        ) + '/checkout.js';
        script.async = true;
        script.onload = resolve;
        document.body.appendChild(script);
    });
};

/**
 * Fetches live pricing data from the Freemius AJAX endpoint.
 *
 * @param {Object} config - Pricing config from window['JkitDashboardOption'].freemius.pricing.
 * @return {Promise<Object>} pricingData — { plugin, plans[], install, reviews }.
 */
export const fetchPricingData = (config, timeout = 10000) => {
    const {
        request_handler_url,
        sandbox,
        s_ctx_type,
        s_ctx_id,
        s_ctx_ts,
        s_ctx_secure,
    } = config;

    const params = { pricing_action: 'fetch_pricing_data', trial: 'false' };
    if (sandbox) params.sandbox = sandbox;
    if (s_ctx_type) params.s_ctx_type = s_ctx_type;
    if (s_ctx_id) params.s_ctx_id = s_ctx_id;
    if (s_ctx_ts) params.s_ctx_ts = s_ctx_ts;
    if (s_ctx_secure) params.s_ctx_secure = s_ctx_secure;

    const url = `${request_handler_url}&${new URLSearchParams(params).toString()}`;

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller ? setTimeout(() => controller.abort(), timeout) : null;

    return fetch(url, { method: 'GET', ...(controller && { signal: controller.signal }) })
        .then((res) => res.json())
        .then((data) => (data.data ? data.data : data))
        .finally(() => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        });
};

/**
 * Formats a numeric price into a currency string.
 *
 * @param {number|null} price
 * @param {string}      currency
 * @return {string|null}
 */
export const formatPrice = (price, currency) => {
    if (price == null) return null;
    const symbol = { usd: '$', eur: '€', gbp: '£' }[currency] || '$';
    return `${symbol}${parseFloat(price).toFixed(2)}`;
};

/**
 * Returns a human-readable label for a license count.
 *
 * @param {number|null} licenses
 * @return {string}
 */
export const getLicensesLabel = (licenses) => {
    if (!licenses) return __('Unlimited Sites', 'jeg-elementor-kit');
    if (licenses === 1) return __('1 Site', 'jeg-elementor-kit');
    return sprintf(__('%d Sites', 'jeg-elementor-kit'), licenses);
};

/**
 * Derives the list of available billing cycles across all plans.
 *
 * @param {Array} plans
 * @return {string[]}
 */
export const getAvailableCycles = (plans) => {
    const cycles = new Set();
    for (const plan of plans) {
        for (const p of (plan.pricing || [])) {
            if (p.monthly_price != null) cycles.add('monthly');
            if (p.annual_price != null) cycles.add('annual');
            if (p.lifetime_price != null) cycles.add('lifetime');
        }
    }
    const order = ['annual', 'monthly', 'lifetime'];
    return order.filter((c) => cycles.has(c));
};
